import React, { useState, useEffect } from 'react';
import AppBar from '@material-ui/core/AppBar';
import { makeStyles } from '@material-ui/core/styles';
import ListIcon from '@material-ui/icons/List';
import AddIcon from '@material-ui/icons/Add';
import PrintIcon from '@material-ui/icons/Print';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import AccountCircle from '@material-ui/icons/AccountCircle';
import MoreIcon from '@material-ui/icons/MoreVert';
import NotificationsIcon from '@material-ui/icons/Notifications';
import CssBaseline from '@material-ui/core/CssBaseline';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import CustomizableSnackbar from '../Snackbar/Snackbar';
import { signOut, sendPasswordResetEmail } from '../../api/usersAPI';
import { getAlerts } from '../../api/alertsAPI';
import SignUpAlert from '../Alerts/SignUpAlert';
import WelcomeAlert from '../Alerts/WelcomeAlert';

const useStyles = makeStyles(theme => ({
  icon: {
    marginRight: theme.spacing(2),
  },
  sectionDesktop: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  sectionMobile: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  grow: {
    flexGrow: 1,
  },
}));

export default function TheAppBar({...props}) {
  const { 
    user,
    hasFamily,
    handlePrint,
    handleAdd,
    isEditing } = props;
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchor2El, setAnchor2El] = useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarVariant, setSnackbarVariant] = useState('info');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarDuration, setSnackbarDuration] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [isAdmin, setIsAdmin] = useState();
  const isMenuOpen = Boolean(anchorEl);
  const isAlertsOpen = Boolean(anchor2El);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const menuId = 'primary-account-menu';
  const alertsId = 'primary-alerts-menu';

  useEffect(() => {
    const initializeAlerts = async () => {
      if (!user) {
        return;
      }
      const isAdmin = user.role === 'admin';
      setIsAdmin(isAdmin);
      const alerts = await getAlerts();
      setAlerts([...alerts]);
    }
    initializeAlerts();
  }, [user]);
  const handleProfileMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleAlertsMenuOpen = event => {
    setAnchor2El(event.currentTarget);
  };
  const handleMobileMenuOpen = event => {
    setMobileMoreAnchorEl(event.currentTarget);
  };
  const handleAlertsMenuClose = () => {
    setAnchor2El(null);
  };
  const handleShowID = () => {
    setSnackbarVariant('info');
    setSnackbarMessage(`ID: ${user.id}`);
    setIsSnackbarOpen(true);
    handleMenuClose();
  }
  const handleSignOut = async () => {
    await signOut();
    window.location = '/signin';
  }
  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
    handleAlertsMenuClose();
  };
  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };
  const handleResetPassword = async () => {
    try {
      await sendPasswordResetEmail();
      setSnackbarVariant('success');
      setSnackbarMessage('Password reset email sent.');
      setIsSnackbarOpen(true);
    } catch (e) {
      setSnackbarVariant('error');
      setSnackbarMessage('Password reset email was unable to be sent. Please try again later.');
      setIsSnackbarOpen(true);
    }
  }
  const handleAppBarPrint = () => {
    handlePrint();
    setSnackbarDuration(1500);
    setSnackbarVariant('info');
    setSnackbarMessage('Adjust scale to ~50% for best results.');
    setIsSnackbarOpen(true);
    setTimeout(() => {
      window.print();
      setSnackbarDuration(null);
    }, 2000);
  }
  const removeAlert = (alertToRemove) => {
    const filtered = alerts.filter((alert) => alert.id !== alertToRemove.id);
    setAlerts([...filtered]);
    handleMenuClose();
  }
  const snackBarSuccess = (message) => {
    setSnackbarVariant('success');
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
  }
  const snackBarError = (message) => {
    setSnackbarVariant('error');
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
  }
  const snackBarWarning = (message) => {
    setSnackbarVariant('warning');
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
  }
  const getAlertsArray = () => {
    const alertsArray = alerts.map((alert) => {
      switch (alert.type) {
        case 'alert/welcome':
          return <WelcomeAlert
            key={alert.id}
            removeAlert={() => removeAlert(alert)}
            alert={alert}
          />
        case 'alert/signup':
          return <SignUpAlert 
            key={alert.id}
            removeAlert={() => removeAlert(alert)} 
            alert={alert}
            snackBarSuccess={snackBarSuccess}
            snackBarError={snackBarError}
            snackBarWarning={snackBarWarning}
          />
        default:
          console.error('Unknown alert type.');
          return <p>Unknown alert type.</p>;
      }
    });
    return alertsArray;
  }
  const renderAlerts = (
    <Menu
      anchorEl={anchor2El}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={alertsId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isAlertsOpen}
      onClose={handleAlertsMenuClose}
    >
      {alerts.length ? 
        getAlertsArray() : (
        <MenuItem onClick={handleMenuClose}>
          <Typography component="p">
            No alerts.
          </Typography>
        </MenuItem>)
      }
    </Menu>
  );
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
      <MenuItem onClick={handleShowID}>My ID</MenuItem>
      <MenuItem onClick={handleResetPassword}>Reset Password</MenuItem>
    </Menu>
  );
  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={handleAlertsMenuOpen}>
        <IconButton aria-label="show new notifications" color="inherit">
          <Badge badgeContent={alerts.length} color="secondary">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );
  return (
    <React.Fragment>
      <CustomizableSnackbar
        open={isSnackbarOpen}
        handleClose={handleSnackbarClose}
        variant={snackbarVariant}
        message={snackbarMessage}
        duration={snackbarDuration}
      />
      <CssBaseline />
      <AppBar position="relative">
        <Toolbar>
          <ListIcon className={classes.icon} />
          <Typography variant="h6" color="inherit" noWrap>
            Gospel Hall Directory
          </Typography>
          <div className={classes.grow} />
          <div className={classes.sectionDesktop}>
            <IconButton 
              disabled={isEditing || (hasFamily && !isAdmin)}
              aria-label="add"
              color="inherit"
              onClick={handleAdd}
            >
              <AddIcon />
            </IconButton>
            <IconButton 
              aria-label="print"
              color="inherit"
              onClick={handleAppBarPrint}
            >
              <PrintIcon />
            </IconButton>
            <IconButton 
              aria-label="show new notifications"
              color="inherit"
              onClick={handleAlertsMenuOpen}
            >
              <Badge badgeContent={alerts.length} color="secondary">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <IconButton
              edge="end"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </ div>
          <div className={classes.sectionMobile}>
            <IconButton
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
      {renderAlerts}
      </React.Fragment>
    );
}